/**
 * EVERY GRADIENT AND PATTERN THIS SCREEN USES, DEFINED ONCE.
 *
 * The KPI strip used to mint one `<linearGradient>` per tile — six identical
 * defs on every render — and the home chart minted a seventh with the same two
 * stops. SVG ids are document-global, so seven copies is also seven chances for
 * two of them to collide and one chart to take another's fill.
 *
 * This renders once, at the top of the screen, in a zero-size `<svg>`. Recharts
 * resolves `fill="url(#dash-area)"` against the DOCUMENT, not against its own
 * chart, so a def outside the chart is found exactly the same way.
 */
import React from 'react';
import { dataViz, palette } from '../../../../../style-utils/theme';
import { GRADIENT } from './chart-theme';

export const ChartDefs: React.FC = () => (
  <svg
    width="0"
    height="0"
    aria-hidden="true"
    focusable="false"
    style={{ position: 'absolute', pointerEvents: 'none' }}
    data-testid="chart-defs"
  >
    <defs>
      {/* Brand teal at 28% down to fully transparent, so the fill reads as
          atmosphere and never competes with the stroke. */}
      <linearGradient id={GRADIENT.area} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={dataViz.areaFrom} />
        <stop offset="100%" stopColor={dataViz.areaTo} />
      </linearGradient>

      {/* The comparison period's fill. Drained on purpose: the earlier period is
          a reference, and giving it the same weight as the current one makes a
          reader compare two equals rather than read one against a baseline. */}
      <linearGradient id={GRADIENT.areaCompare} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={palette.lineStrong} stopOpacity={0.35} />
        <stop offset="100%" stopColor={palette.lineStrong} stopOpacity={0} />
      </linearGradient>

      {/* A HATCH, NOT A FILL, for "nobody ever recorded this".
          A unit nobody has entered an occupancy for is indistinguishable from an
          empty one from the outside, and only one of those is a fact. A solid
          fill would read as a third kind of tenancy; absence has to look like
          absence. */}
      <pattern
        id={GRADIENT.hatchUnrecorded}
        width="6"
        height="6"
        patternUnits="userSpaceOnUse"
        patternTransform="rotate(45)"
      >
        <rect width="6" height="6" fill={palette.surfaceMuted} />
        <line x1="0" y1="0" x2="0" y2="6" stroke={palette.lineStrong} strokeWidth="2" />
      </pattern>

      {/* The ring around an anomaly marker. A soft outer glow rather than a
          second colour, because the point already carries its series colour and
          a recoloured point would read as a different series. */}
      <filter id={GRADIENT.anomalyGlow} x="-60%" y="-60%" width="220%" height="220%">
        <feDropShadow
          dx="0"
          dy="0"
          stdDeviation="3"
          floodColor={palette.warning}
          floodOpacity="0.85"
        />
      </filter>
    </defs>
  </svg>
);
